/**
 * advance-game-end.ts — per-tick win/defeat detection (v3M12).
 *
 * Runs after combat / surface-batteries / bombardment so a planet
 * flip on this tick (Loyalty conquest or depopulation) can trigger
 * an empire elimination → outcome flip on the same tick. Per
 * `docs/WIN_CONDITIONS.md`:
 *
 *   - For each empire with zero planets owned, despawn its ships
 *     (keeps the empire row in `state.empires` so end-game stats
 *     can still attribute names + colours, but clears `shipIds`
 *     and removes the entries from `state.ships` + `teamVision`).
 *   - If the player's empire has zero planets → `gameOutcome` flips
 *     to `'defeat'`.
 *   - Else if every non-player empire has zero planets → `gameOutcome`
 *     flips to `'victory'`.
 *
 * Once `gameOutcome` is non-`'in-progress'`, the top-of-`advanceTick`
 * guard makes the whole sim a no-op — this pass becomes irrelevant.
 *
 * Determinism: pure derivation from planet ownership + empire
 * membership. No RNG, no wall-clock, no input.
 */

import type { EmpireId, GameState, ShipInstanceId } from '../state/types';
import { EMPIRE_NEUTRAL_ID } from '../state/types';

export function advanceGameEnd(state: GameState): void {
  if (state.gameOutcome !== 'in-progress') return;

  // Tally planets per empire.
  const planetsPerEmpire = new Map<EmpireId, number>();
  for (const empire of state.empires.values()) planetsPerEmpire.set(empire.id, 0);
  for (const planet of state.planets.values()) {
    if (planet.ownerEmpireId === null) continue;
    const cur = planetsPerEmpire.get(planet.ownerEmpireId) ?? 0;
    planetsPerEmpire.set(planet.ownerEmpireId, cur + 1);
  }

  // Despawn ships of any empire that's been eliminated. Idempotent —
  // an already-eliminated empire (shipIds: []) loops zero times.
  for (const empire of state.empires.values()) {
    if ((planetsPerEmpire.get(empire.id) ?? 0) > 0) continue;
    if (empire.shipIds.length === 0) continue;
    despawnEmpireShips(state, empire.id);
  }

  // Outcome flip — defeat takes precedence so a player whose own
  // planets fall the same tick they wipe out the last AI sees a
  // defeat screen, not a victory.
  const playerPlanets = planetsPerEmpire.get(state.playerEmpireId) ?? 0;
  if (playerPlanets === 0) {
    state.gameOutcome = 'defeat';
    return;
  }
  // Victory only fires when the empire map has at least one non-player
  // empire AND none of them currently hold planets. The
  // `hasAnyNonPlayerEmpire` guard is what keeps single-empire test
  // fixtures (and the unlikely solo-empire scenario) from freezing
  // the sim on tick 1: without enemies to eliminate, "victory" has
  // no semantic meaning — the game just runs.
  let hasAnyNonPlayerEmpire = false;
  let anyEnemyAlive = false;
  for (const empire of state.empires.values()) {
    if (empire.id === state.playerEmpireId) continue;
    // v3M21.1 — synthetic neutral empire is not an "enemy" for victory
    // purposes. Without this filter a galaxy with seeded neutrals would
    // never trigger victory: the neutral's planets would keep
    // anyEnemyAlive=true forever even after every real AI is wiped.
    if (empire.id === EMPIRE_NEUTRAL_ID) continue;
    hasAnyNonPlayerEmpire = true;
    if ((planetsPerEmpire.get(empire.id) ?? 0) > 0) {
      anyEnemyAlive = true;
      break;
    }
  }
  if (hasAnyNonPlayerEmpire && !anyEnemyAlive) {
    state.gameOutcome = 'victory';
  }
}

/**
 * Remove every ship owned by the given empire from `state.ships`
 * and from every team's `teamVision` row, then clear the empire's
 * own `shipIds`. The empire row stays in `state.empires` so the
 * end-game overlay can still attribute it by name + colour.
 */
function despawnEmpireShips(state: GameState, empireId: EmpireId): void {
  const empire = state.empires.get(empireId);
  if (!empire) return;
  const ids: ShipInstanceId[] = [...empire.shipIds];
  for (const id of ids) state.ships.delete(id);
  for (const inner of state.teamVision.values()) {
    for (const id of ids) inner.delete(id);
  }
  empire.shipIds = [];
}
